import { callFunction } from "./functions-client";

// Product feedback (rating + type + triage), distinct from support tickets.
export const FEEDBACK_TYPES = ["general", "bug", "feature", "praise"] as const;
export const FEEDBACK_STATUSES = ["new", "triaged", "planned", "shipped", "declined"] as const;

export interface FeedbackAttachment { id: string; content_type: string; url: string | null }

export interface FeedbackItem {
  id: string;
  type: string;
  rating: number | null;
  message: string;
  context: Record<string, unknown>;
  status: string;
  created_at: string;
  attachments?: FeedbackAttachment[];
}

// Attachments: images only. The client checks these for UX; the server re-validates every
// file by magic bytes (PNG/JPEG/GIF/WebP) and rejects anything else — the security boundary.
export const MAX_FEEDBACK_IMAGES = 10;
export const MAX_FEEDBACK_IMAGE_BYTES = 10 * 1024 * 1024;
export const ACCEPTED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"];

export const submitFeedback = (input: { type: string; rating?: number | null; message: string; context?: Record<string, string> }) =>
  callFunction<{ ok: boolean; id: string }>("feedback/submit", { body: input });

export const feedbackAttachmentUrl = (feedback_id: string) =>
  callFunction<{ path: string; upload_url: string }>("feedback/attachment-url", { body: { feedback_id } });

export const finalizeFeedbackAttachment = (feedback_id: string, path: string) =>
  callFunction<{ ok: boolean }>("feedback/attachment-finalize", { body: { feedback_id, path } });

/** Upload one screenshot to a feedback: request a slot, PUT the bytes, finalize (server
 *  validates magic bytes). Returns true if the server accepted it as a real image. */
export async function uploadFeedbackImage(feedbackId: string, file: File): Promise<boolean> {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type) || file.size > MAX_FEEDBACK_IMAGE_BYTES) return false;
  const { path, upload_url } = await feedbackAttachmentUrl(feedbackId);
  const put = await fetch(upload_url, { method: "PUT", headers: { "content-type": file.type }, body: file });
  if (!put.ok) return false;
  try { await finalizeFeedbackAttachment(feedbackId, path); return true; } catch { return false; }
}

export const getRoadmap = () =>
  callFunction<{ items: { id: string; message: string; status: string; created_at: string }[] }>("feedback/roadmap", { method: "GET" });

export const adminListFeedback = (opts?: { status?: string; type?: string }) => {
  const p = new URLSearchParams();
  if (opts?.status) p.set("status", opts.status);
  if (opts?.type) p.set("type", opts.type);
  const qs = p.toString();
  return callFunction<{ feedback: FeedbackItem[]; counts: Record<string, number> }>(`feedback/admin-list${qs ? `?${qs}` : ""}`, { method: "GET" });
};

export const adminUpdateFeedback = (feedback_id: string, status: string) =>
  callFunction<{ ok: boolean }>("feedback/admin-update", { body: { feedback_id, status } });
